import React from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  AreaChart, Area, PieChart, Pie, Cell, FunnelChart, Funnel, LabelList,
} from 'recharts';
import { PALETTE, SERIES } from './reportShared';
import type {
  DepartmentPerformance, SkillScore, CompletionTrendData, CertificationDistribution,
  EnrollmentFunnel, TimeDistribution, HeatmapCell, LevelDistribution, AssessmentScores,
  CohortRow, CertificationExpiry, TeamPerformance,
} from '@/services/PagesServices/SuperAdminReportService';

const TOOLTIP_STYLE = {
  borderRadius: 10,
  border: '1px solid #e5e7eb',
  boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
  fontSize: 12,
};
const AXIS = { fontSize: 11, fill: '#9ca3af' };

// ---- Department performance (grouped bar) ---------------------------------
export const DepartmentBar: React.FC<{ data: DepartmentPerformance[] }> = ({ data }) => (
  <ResponsiveContainer width="100%" height={280}>
    <BarChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 4 }} barGap={4}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f3f5" />
      <XAxis dataKey="department" tick={AXIS} tickLine={false} axisLine={false} interval={0} angle={-12} textAnchor="end" height={50} />
      <YAxis tick={AXIS} tickLine={false} axisLine={false} unit="%" />
      <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: any, n: any) => [`${v}%`, n === 'completedPercentage' ? 'Engaged' : 'Coverage']} />
      <Legend iconType="circle" formatter={(v) => (v === 'completedPercentage' ? 'Engaged learners' : 'Enrolment coverage')} wrapperStyle={{ fontSize: 12 }} />
      <Bar dataKey="completedPercentage" fill={PALETTE.green} radius={[4, 4, 0, 0]} maxBarSize={26} />
      <Bar dataKey="totalPercentage" fill={PALETTE.blue} radius={[4, 4, 0, 0]} maxBarSize={26} />
    </BarChart>
  </ResponsiveContainer>
);

// ---- Skills radar (generic N axes) ----------------------------------------
export const SkillsRadar: React.FC<{ data: SkillScore[] }> = ({ data }) => (
  <ResponsiveContainer width="100%" height={280}>
    <RadarChart data={data} outerRadius="72%">
      <PolarGrid stroke="#e5e7eb" />
      <PolarAngleAxis dataKey="skill" tick={{ fontSize: 11, fill: '#6b7280' }} />
      <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9, fill: '#cbd5e1' }} />
      <Radar name="Avg score" dataKey="score" stroke={PALETTE.amber} fill={PALETTE.amber} fillOpacity={0.45} />
      <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: any) => [`${v}%`, 'Avg score']} />
    </RadarChart>
  </ResponsiveContainer>
);

// ---- Completion trends (area) ---------------------------------------------
export const CompletionArea: React.FC<{ data: CompletionTrendData[] }> = ({ data }) => (
  <ResponsiveContainer width="100%" height={280}>
    <AreaChart data={data} margin={{ top: 8, right: 12, left: -18, bottom: 4 }}>
      <defs>
        <linearGradient id="gradCompleted" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={PALETTE.green} stopOpacity={0.5} />
          <stop offset="100%" stopColor={PALETTE.green} stopOpacity={0.04} />
        </linearGradient>
        <linearGradient id="gradEnrolled" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={PALETTE.blue} stopOpacity={0.35} />
          <stop offset="100%" stopColor={PALETTE.blue} stopOpacity={0.04} />
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f3f5" />
      <XAxis dataKey="month" tick={AXIS} tickLine={false} axisLine={false} />
      <YAxis tick={AXIS} tickLine={false} axisLine={false} />
      <Tooltip contentStyle={TOOLTIP_STYLE} />
      <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
      <Area type="monotone" dataKey="enrolled" name="Enrolled" stroke={PALETTE.blue} strokeWidth={2} fill="url(#gradEnrolled)" />
      <Area type="monotone" dataKey="completed" name="Completed" stroke={PALETTE.green} strokeWidth={2} fill="url(#gradCompleted)" />
      <Area type="monotone" dataKey="target" name="Target" stroke={PALETTE.amber} strokeWidth={2} strokeDasharray="5 5" fill="none" />
    </AreaChart>
  </ResponsiveContainer>
);

// ---- Certification distribution (donut) -----------------------------------
export const CertPie: React.FC<{ data: CertificationDistribution[] }> = ({ data }) => (
  <ResponsiveContainer width="100%" height={280}>
    <PieChart>
      <Pie data={data} dataKey="percentage" nameKey="type" cx="50%" cy="50%" innerRadius={58} outerRadius={92} paddingAngle={2}>
        {data.map((d, i) => <Cell key={i} fill={d.color || SERIES[i % SERIES.length]} />)}
      </Pie>
      <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: any, n: any) => [`${v}%`, n]} />
      <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
    </PieChart>
  </ResponsiveContainer>
);

// ---- Enrollment funnel -----------------------------------------------------
export const FunnelViz: React.FC<{ data: EnrollmentFunnel }> = ({ data }) => {
  const rows = [
    { name: 'Enrolled', value: data.enrolled, fill: PALETTE.blue },
    { name: 'Started', value: data.started, fill: PALETTE.teal },
    { name: 'Completed', value: data.completed, fill: PALETTE.green },
    { name: 'Certified', value: data.certified, fill: PALETTE.amber },
  ];
  return (
    <ResponsiveContainer width="100%" height={280}>
      <FunnelChart>
        <Tooltip contentStyle={TOOLTIP_STYLE} />
        <Funnel dataKey="value" data={rows} isAnimationActive>
          <LabelList position="right" dataKey="name" fill="#374151" stroke="none" fontSize={12} />
          <LabelList position="left" dataKey="value" fill="#111827" stroke="none" fontSize={12} fontWeight={700} />
        </Funnel>
      </FunnelChart>
    </ResponsiveContainer>
  );
};

// ---- Time-spent histogram --------------------------------------------------
export const TimeHistogram: React.FC<{ data: TimeDistribution }> = ({ data }) => (
  <ResponsiveContainer width="100%" height={280}>
    <BarChart data={data.buckets} margin={{ top: 8, right: 8, left: -18, bottom: 4 }}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f3f5" />
      <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={false} />
      <YAxis tick={AXIS} tickLine={false} axisLine={false} allowDecimals={false} />
      <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: any) => [`${v} learners`, 'Learners']} labelFormatter={(l) => `${l} min`} />
      <Bar dataKey="learners" fill={PALETTE.purple} radius={[4, 4, 0, 0]} maxBarSize={48} />
    </BarChart>
  </ResponsiveContainer>
);

// ---- Level distribution (donut) -------------------------------------------
export const LevelDonut: React.FC<{ data: LevelDistribution }> = ({ data }) => {
  const rows = data.levels.map((l) => ({ name: `Level ${l.level}`, value: l.learners }));
  return (
    <ResponsiveContainer width="100%" height={240}>
      <PieChart>
        <Pie data={rows} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={48} outerRadius={80} paddingAngle={2}>
          {rows.map((_, i) => <Cell key={i} fill={SERIES[i % SERIES.length]} />)}
        </Pie>
        <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: any, n: any) => [`${v} learners`, n]} />
        <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  );
};

// ---- Assessment score distribution ----------------------------------------
export const AssessmentDist: React.FC<{ data: AssessmentScores }> = ({ data }) => {
  const barColor = (label: string) => {
    if (label === '0-20' || label === '20-40') return PALETTE.red;
    if (label === '40-60') return PALETTE.amber;
    return PALETTE.green;
  };
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data.buckets} margin={{ top: 8, right: 8, left: -18, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f3f5" />
        <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={false} unit="%" />
        <YAxis tick={AXIS} tickLine={false} axisLine={false} allowDecimals={false} />
        <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: any) => [`${v} responses`, 'Responses']} labelFormatter={(l) => `Score ${l}%`} />
        <Bar dataKey="responses" radius={[4, 4, 0, 0]} maxBarSize={48}>
          {data.buckets.map((b, i) => <Cell key={i} fill={barColor(b.label)} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

// ---- Activity heatmap (custom grid: day-of-week × 3-hour block) -------------
const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const BLOCK_LABELS = ['12a', '3a', '6a', '9a', '12p', '3p', '6p', '9p'];
export const ActivityHeatmap: React.FC<{ data: HeatmapCell[] }> = ({ data }) => {
  // 7 rows (dow) × 8 columns (3-hour blocks)
  const grid: number[][] = Array.from({ length: 7 }, () => Array(8).fill(0));
  let max = 0;
  for (const c of data) {
    const row = ((c.dow - 1) % 7 + 7) % 7;
    const col = Math.min(7, Math.floor(c.hour / 3));
    grid[row][col] += c.count;
    if (grid[row][col] > max) max = grid[row][col];
  }
  const shade = (v: number) => {
    if (v === 0) return '#f1f5f9';
    const t = max ? v / max : 0;
    // interpolate light→sunflare orange
    const a = 0.15 + t * 0.85;
    return `rgba(249, 115, 22, ${a.toFixed(2)})`;
  };
  return (
    <div className="s-admin-heatx">
      <div className="s-admin-heatx-grid">
        <div className="s-admin-heatx-corner" />
        {BLOCK_LABELS.map((b) => <div key={b} className="s-admin-heatx-col-label">{b}</div>)}
        {grid.map((rowVals, r) => (
          <React.Fragment key={r}>
            <div className="s-admin-heatx-row-label">{DOW_LABELS[r]}</div>
            {rowVals.map((v, c) => (
              <div key={c} className="s-admin-heatx-cell" style={{ background: shade(v) }} title={`${DOW_LABELS[r]} ${BLOCK_LABELS[c]}: ${v} logins`} />
            ))}
          </React.Fragment>
        ))}
      </div>
      <div className="s-admin-heatx-legend">
        <span>Less</span>
        <span className="s-admin-heatx-swatch" style={{ background: '#f1f5f9' }} />
        <span className="s-admin-heatx-swatch" style={{ background: 'rgba(249,115,22,0.4)' }} />
        <span className="s-admin-heatx-swatch" style={{ background: 'rgba(249,115,22,0.7)' }} />
        <span className="s-admin-heatx-swatch" style={{ background: 'rgba(249,115,22,1)' }} />
        <span>More</span>
      </div>
    </div>
  );
};

// ---- Cohort retention (custom grid heatmap) --------------------------------
export const CohortHeatmap: React.FC<{ data: CohortRow[] }> = ({ data }) => {
  const cols: Array<{ key: keyof CohortRow; label: string }> = [
    { key: 'enrolledPct', label: 'Enrolled' },
    { key: 'completedPct', label: 'Completed' },
    { key: 'activePct', label: 'Active 30d' },
  ];
  const shade = (v: number) => {
    if (v === 0) return '#f1f5f9';
    const t = v / 100;
    const a = 0.15 + t * 0.85;
    return `rgba(59, 130, 246, ${a.toFixed(2)})`;
  };
  return (
    <div className="s-admin-cohortx">
      <div className="s-admin-cohortx-grid">
        <div className="s-admin-cohortx-corner">Cohort</div>
        {cols.map((c) => <div key={c.key} className="s-admin-cohortx-col-label">{c.label}</div>)}
        {data.map((row) => (
          <React.Fragment key={row.cohort}>
            <div className="s-admin-cohortx-row-label">
              {row.cohort}<span className="s-admin-cohortx-size">n={row.size}</span>
            </div>
            {cols.map((c) => {
              const v = row[c.key] as number;
              return (
                <div key={c.key} className="s-admin-cohortx-cell" style={{ background: shade(v), color: v > 55 ? '#fff' : '#334155' }}>
                  {v}%
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// ---- Certification expiry list --------------------------------------------
export const CertExpiryPanel: React.FC<{ data: CertificationExpiry }> = ({ data }) => (
  <div className="s-admin-certx">
    <div className="s-admin-certx-stats">
      <div className="s-admin-certx-stat"><span className="dot green" />{data.valid}<small>Valid</small></div>
      <div className="s-admin-certx-stat"><span className="dot amber" />{data.expiringSoon}<small>Expiring ≤30d</small></div>
      <div className="s-admin-certx-stat"><span className="dot red" />{data.expired}<small>Expired</small></div>
    </div>
    <div className="s-admin-certx-list">
      {data.upcoming.length === 0 ? (
        <div className="s-admin-certx-none">No certificates expiring in the next 60 days 🎉</div>
      ) : (
        data.upcoming.map((u, i) => (
          <div key={i} className="s-admin-certx-row">
            <div className="s-admin-certx-info">
              <span className="s-admin-certx-name">{u.name}</span>
              <span className="s-admin-certx-learner">{u.learner}</span>
            </div>
            <span className={`s-admin-certx-days ${u.daysLeft <= 30 ? 'urgent' : ''}`}>{u.daysLeft}d left</span>
          </div>
        ))
      )}
    </div>
  </div>
);

// ---- Streak leaderboard ----------------------------------------------------
export const StreakList: React.FC<{ data: LevelDistribution }> = ({ data }) => (
  <div className="s-admin-streakx">
    {data.topStreaks.length === 0 ? (
      <div className="s-admin-certx-none">No active streaks yet</div>
    ) : (
      data.topStreaks.map((s, i) => (
        <div key={i} className="s-admin-streakx-row">
          <span className="s-admin-streakx-rank">{i + 1}</span>
          <span className="s-admin-streakx-name">{s.name}</span>
          <span className="s-admin-streakx-fire">🔥 {s.currentStreak}</span>
          <span className="s-admin-streakx-best">best {s.longestStreak}</span>
        </div>
      ))
    )}
  </div>
);

// ---- Team performance table ------------------------------------------------
export const TeamTable: React.FC<{ data: TeamPerformance[] }> = ({ data }) => (
  <div className="s-admin-teamx">
    <table className="s-admin-teamx-table">
      <thead>
        <tr><th>Team</th><th>Members</th><th>Completion</th><th>Active/wk</th><th>Avg Pts</th></tr>
      </thead>
      <tbody>
        {data.map((t, i) => (
          <tr key={i}>
            <td className="s-admin-teamx-name">{t.team}</td>
            <td>{t.members}</td>
            <td>
              <div className="s-admin-teamx-bar-wrap">
                <div className="s-admin-teamx-bar" style={{ width: `${t.completionRate}%` }} />
                <span>{t.completionRate}%</span>
              </div>
            </td>
            <td>{t.activeThisWeek}</td>
            <td>{t.avgPoints.toLocaleString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
